
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ViewProposalDialog } from '@/components/sales/ViewProposalDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search,
  FileText,
  MoreVertical,
  Plus,
  DollarSign,
  Users,
  Calendar,
  Send,
  Pencil,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { proposalService, Proposal, ProposalFormData } from '@/services/proposal.service';
import { clientService, Client } from '@/services/client.service';
import { useAuthStore } from '@/stores/authStore';


const getStatusColor = (status: string) => {
  switch (status) {
    case 'Draft':
      return 'bg-gray-100 text-gray-800';
    case 'Sent':
      return 'bg-blue-100 text-blue-800';
    case 'Accepted':
      return 'bg-green-100 text-green-800';
    case 'Rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default function Proposals() {
  const { hasSubModulePermission } = useAuthStore();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProposals, setTotalProposals] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selected, setSelected] = useState<Proposal | null>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [newProposalClientId, setNewProposalClientId] = useState<string>('');
  const [editProposalClientId, setEditProposalClientId] = useState<string>('');

  const [newProposal, setNewProposal] = useState<ProposalFormData>({
    title: '',
    client: '',
    value: 0,
    status: 'Draft',
    sentDate: null,
    validUntil: '',
    version: '1.0',
  });

  const [editProposal, setEditProposal] = useState<ProposalFormData>({
    title: '',
    client: '',
    value: 0,
    status: 'Draft',
    sentDate: null,
    validUntil: '',
    version: '1.0',
  });

  const [newProposalErrors, setNewProposalErrors] = useState({
    title: '',
    client: '',
    validUntil: '',
  });
  const [editProposalErrors, setEditProposalErrors] = useState({
    title: '',
    client: '',
    validUntil: '',
  });

  useEffect(() => {
    loadProposals();
  }, [currentPage, pageSize, searchQuery]);

  useEffect(() => {
    if (showAdd || showEdit) {
      loadClients();
    }
  }, [showAdd, showEdit]);

  useEffect(() => {
    if (showEdit && selected && clients.length > 0) {
      const foundClient = clients.find(c => c.name === selected.client);
      if (foundClient) {
        setEditProposalClientId(foundClient.id || foundClient._id || '');
      }
    }
  }, [showEdit, selected, clients]);

  const loadProposals = async () => {
    try {
      setLoading(true);
      const response = await proposalService.getProposals({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
      });

      setProposals(response.data.map(proposal => ({
        ...proposal,
        id: proposal._id || proposal.id,
      })));
      setTotalPages(Math.ceil(response.total / pageSize));
      setTotalProposals(response.total);
    } catch (error) {
      console.error('Error loading proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const clientsResponse = await clientService.getClients({ limit: 1000 });
      const clientsData = clientsResponse.data || clientsResponse || [];

      if (Array.isArray(clientsData) && clientsData.length > 0) {
        setClients(clientsData);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setClients([]);
      toast.error('Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const startIndex = proposals.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIndex = proposals.length === 0 ? 0 : Math.min(startIndex + pageSize, totalProposals || proposals.length);

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchQuery("");
    await loadProposals();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const debouncedSearch = (() => {
    let timeoutId: NodeJS.Timeout;
    return (query: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(query);
        setCurrentPage(1);
      }, 500);
    };
  })();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const openEdit = (p: Proposal) => {
    setSelected(p);
    setEditProposal({
      title: p.title,
      client: p.client,
      value: p.value,
      status: p.status,
      sentDate: p.sentDate,
      validUntil: p.validUntil,
      version: p.version,
    });
    setEditProposalClientId('');
    setShowEdit(true);
  };

  const openDelete = (p: Proposal) => {
    setSelected(p);
    setShowDelete(true);
  };

  const openView = (p: Proposal) => {
    setSelected(p);
    setShowView(true);
  };

  const handleCreate = async () => {
    const selectedClient = clients.find(c => (c.id === newProposalClientId) || (c._id === newProposalClientId));
    const clientName = selectedClient ? selectedClient.name : '';

    const errors = {
      title: !newProposal.title ? 'Title is required' : '',
      client: !newProposalClientId ? 'Client is required' : '',
      validUntil: !newProposal.validUntil ? 'Valid Until is required' : '',
    };
    setNewProposalErrors(errors);

    if (!newProposal.title || !newProposalClientId || !newProposal.validUntil) {
      return;
    }

    try {
      setSubmitting(true);
      const proposalData = {
        ...newProposal,
        client: clientName,
      };
      await proposalService.createProposal(proposalData);

      setNewProposal({
        title: '',
        client: '',
        value: 0,
        status: 'Draft',
        sentDate: null,
        validUntil: '',
        version: '1.0',
      });
      setNewProposalClientId('');
      setNewProposalErrors({ title: '', client: '', validUntil: '' });
      setShowAdd(false);

      await loadProposals();

      toast.success("Proposal has been created successfully.");
    } catch (error: any) {
      console.error('Error creating proposal:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;

    const selectedClient = clients.find(c => (c.id === editProposalClientId) || (c._id === editProposalClientId));
    const clientName = selectedClient ? selectedClient.name : '';

    if (!editProposal.title || !editProposalClientId || !editProposal.validUntil) {
      return;
    }

    try {
      setSubmitting(true);
      const proposalData = {
        ...editProposal,
        client: clientName,
      };
      await proposalService.updateProposal(selected.id, proposalData);

      setShowEdit(false);

      await loadProposals();

      toast.success("Proposal has been updated successfully.");
    } catch (error: any) {
      console.error('Error updating proposal:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;

    try {
      setSubmitting(true);
      await proposalService.deleteProposal(selected.id);

      setShowDelete(false);
      setSelected(null);

      await loadProposals();

      toast.success(`${selected.title} has been deleted successfully.`);
    } catch (error: any) {
      console.error('Error deleting proposal:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendProposal = async (proposal: Proposal) => {
    try {
      await proposalService.sendProposal(proposal.id);
      await loadProposals();

      toast.success(`${proposal.title} has been sent successfully.`);
    } catch (error: any) {
      console.error('Error sending proposal:', error);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Proposals</h1>
          <p className="text-muted-foreground">
            Create and manage client proposals
          </p>
        </div>
        {hasSubModulePermission('sales_clients', 'proposal', 'create') && (
          <Button onClick={() => setShowAdd(true)} className="rounded-xl">
            <Plus className="h-4 w-4 mr-2" />
            Create Proposal
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Proposals</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4</div>
            <p className="text-xs text-muted-foreground">
              Active proposals
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$185,000</div>
            <p className="text-xs text-muted-foreground">
              Combined value
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acceptance Rate</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">25%</div>
            <p className="text-xs text-muted-foreground">
              Last 30 days
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Response Time</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3.5 days</div>
            <p className="text-xs text-muted-foreground">
              From sent to response
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Proposal List</CardTitle>
              <CardDescription>View and manage your proposals</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search proposals..."
                  className="pl-8"
                  onChange={handleSearchChange}
                />
              </div>
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[120px] rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 rows</SelectItem>
                  <SelectItem value="10">10 rows</SelectItem>
                  <SelectItem value="25">25 rows</SelectItem>
                  <SelectItem value="50">50 rows</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={handleRefresh} className="rounded-xl">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent Date</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading proposals...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : proposals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No proposals found
                    </TableCell>
                  </TableRow>
                ) : (
                  proposals.map((proposal) => (
                    <TableRow key={proposal.id}>
                      <TableCell className="font-medium">{proposal.title}</TableCell>
                      <TableCell>{proposal.client}</TableCell>
                      <TableCell>${proposal.value.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(proposal.status)}>
                          {proposal.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{proposal.sentDate || '-'}</TableCell>
                      <TableCell>{proposal.validUntil}</TableCell>
                      <TableCell>v{proposal.version}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openView(proposal)}>
                              <FileText className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {hasSubModulePermission('sales_clients', 'proposal', 'edit') && (
                              <DropdownMenuItem onClick={() => openEdit(proposal)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Proposal
                              </DropdownMenuItem>
                            )}
                            {hasSubModulePermission('sales_clients', 'proposal', 'delete') && (
                              <DropdownMenuItem onClick={() => openDelete(proposal)} className="text-red-600 focus:text-red-600">
                                <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                                Delete Proposal
                              </DropdownMenuItem>
                            )}
                            {proposal.status === 'Draft' && (
                              <DropdownMenuItem onClick={() => handleSendProposal(proposal)}>
                                <Send className="h-4 w-4 mr-2" />
                                Send to Client
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <div className="flex items-center justify-between px-6 pb-4">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, totalProposals)} of {totalProposals} entries
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded-xl"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {(() => {
                const pageButtons: JSX.Element[] = [];

                if (totalPages > 3) {
                  let startPage = Math.max(1, currentPage - 1);
                  let endPage = Math.min(totalPages, currentPage + 1);

                  if (startPage === 1) {
                    endPage = Math.min(totalPages, 3);
                  }
                  if (endPage === totalPages) {
                    startPage = Math.max(1, totalPages - 2);
                  }

                  if (startPage > 1) {
                    pageButtons.push(
                      <Button
                        key={1}
                        variant={1 === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        1
                      </Button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis-start" className="px-2 text-muted-foreground">
                          ..
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis-end" className="px-2 text-muted-foreground">
                          ..
                        </span>
                      );
                    }
                    pageButtons.push(
                      <Button
                        key={totalPages}
                        variant={totalPages === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    );
                  }
                } else {
                  for (let i = 1; i <= totalPages; i++) {
                    pageButtons.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    );
                  }
                }

                return pageButtons;
              })()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="rounded-xl"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </Card>

      {/* View Proposal Dialog */}
      <Dialog open={showView} onOpenChange={setShowView}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Proposal Details</DialogTitle>
          </DialogHeader>
          {selected && (
            <ViewProposalDialog proposal={selected} open={showView} onOpenChange={setShowView} />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Proposal Modal */}
      <Dialog open={showAdd} onOpenChange={(open) => {
        setShowAdd(open);
        if (!open) {
          setNewProposalErrors({ title: '', client: '', validUntil: '' });
          setNewProposalClientId('');
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Create Proposal</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-[5px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prop-title">Title *</Label>
                  <Input id="prop-title" placeholder="Enter proposal title" value={newProposal.title} onChange={(e) => { setNewProposal({ ...newProposal, title: e.target.value }); setNewProposalErrors(prev => ({ ...prev, title: '' })); }} className="rounded-xl" />
                  {newProposalErrors.title && <div className="text-red-600 text-xs mt-1">{newProposalErrors.title}</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prop-client">Client *</Label>
                  <Select
                    value={newProposalClientId}
                    onValueChange={(value) => {
                      setNewProposalClientId(value);
                      setNewProposalErrors(prev => ({ ...prev, client: '' }));
                    }}
                  >
                    <SelectTrigger className="w-full h-10 border-gray-300 rounded-xl">
                      <SelectValue placeholder="Select and begin typing" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingClients ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          Loading clients from API...
                        </div>
                      ) : clients.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No clients available from API
                        </div>
                      ) : (
                        clients.map((client) => {
                          const clientId = client.id || client._id || '';
                          return (
                            <SelectItem key={clientId} value={clientId}>
                              <div className="flex items-center justify-between w-full">
                                <span className="font-medium">{client.name}</span>
                                <span className="text-xs text-gray-500 ml-2">{client.email}</span>
                              </div>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  {newProposalErrors.client && <div className="text-red-600 text-xs mt-1">{newProposalErrors.client}</div>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Value ($)</Label>
                  <Input type="number" min="0" step="0.01" value={newProposal.value} onChange={(e) => setNewProposal({ ...newProposal, value: parseFloat(e.target.value) || 0 })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newProposal.status} onValueChange={(v: 'Draft' | 'Sent' | 'Accepted' | 'Rejected') => setNewProposal({ ...newProposal, status: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Sent">Sent</SelectItem>
                      <SelectItem value="Accepted">Accepted</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Owner field removed */}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Sent Date</Label>
                  <Input type="date" value={newProposal.sentDate || ''} onChange={(e) => setNewProposal({ ...newProposal, sentDate: e.target.value || null })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Valid Until *</Label>
                  <Input type="date" value={newProposal.validUntil} onChange={(e) => { setNewProposal({ ...newProposal, validUntil: e.target.value }); setNewProposalErrors(prev => ({ ...prev, validUntil: '' })); }} className="rounded-xl" />
                  {newProposalErrors.validUntil && <div className="text-red-600 text-xs mt-1">{newProposalErrors.validUntil}</div>}
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input value={newProposal.version} onChange={(e) => setNewProposal({ ...newProposal, version: e.target.value })} className="rounded-xl" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-xl">Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Proposal Modal */}
      <Dialog open={showEdit} onOpenChange={(open) => {
        setShowEdit(open);
        if (!open) {
          setEditProposalErrors({ title: '', client: '', validUntil: '' });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900">Edit Proposal</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-[5px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-prop-title">Title *</Label>
                  <Input id="edit-prop-title" placeholder="Enter proposal title" value={editProposal.title} onChange={(e) => setEditProposal({ ...editProposal, title: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-prop-client">Client *</Label>
                  <Select
                    value={editProposalClientId}
                    onValueChange={(value) => {
                      setEditProposalClientId(value);
                      setEditProposalErrors(prev => ({ ...prev, client: '' }));
                    }}
                  >
                    <SelectTrigger className="w-full h-10 border-gray-300 rounded-xl">
                      <SelectValue placeholder="Select and begin typing" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingClients ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          Loading clients from API...
                        </div>
                      ) : clients.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500 text-center">
                          No clients available from API
                        </div>
                      ) : (
                        clients.map((client) => {
                          const clientId = client.id || client._id || '';
                          return (
                            <SelectItem key={clientId} value={clientId}>
                              <div className="flex items-center justify-between w-full">
                                <span className="font-medium">{client.name}</span>
                                <span className="text-xs text-gray-500 ml-2">{client.email}</span>
                              </div>
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>
                  {editProposalErrors.client && <div className="text-red-600 text-xs mt-1">{editProposalErrors.client}</div>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Value ($)</Label>
                  <Input type="number" min="0" step="0.01" value={editProposal.value} onChange={(e) => setEditProposal({ ...editProposal, value: parseFloat(e.target.value) || 0 })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editProposal.status} onValueChange={(v: 'Draft' | 'Sent' | 'Accepted' | 'Rejected') => setEditProposal({ ...editProposal, status: v })}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Sent">Sent</SelectItem>
                      <SelectItem value="Accepted">Accepted</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Owner field removed */}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Sent Date</Label>
                  <Input type="date" value={editProposal.sentDate || ''} onChange={(e) => setEditProposal({ ...editProposal, sentDate: e.target.value || null })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Valid Until *</Label>
                  <Input type="date" value={editProposal.validUntil} onChange={(e) => setEditProposal({ ...editProposal, validUntil: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input value={editProposal.version} onChange={(e) => setEditProposal({ ...editProposal, version: e.target.value })} className="rounded-xl" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowEdit(false)} className="rounded-xl">Cancel</Button>
            <Button
              onClick={handleUpdate}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Proposal?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete
              {selected ? ` ${selected.title}` : ' this proposal'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 